import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { Sun, Moon, LayoutDashboard, Building2, Users, Link2, Send, Megaphone, Map as MapIcon } from 'lucide-react';

const adminNavItems = [
  { to: '/admin/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/admin/organizations', label: 'Organizations', Icon: Building2 },
  { to: '/admin/marketers', label: 'Marketers', Icon: Megaphone },
  { to: '/admin/users', label: 'Users', Icon: Users },
  { to: '/admin/map', label: 'Map', Icon: MapIcon },
  { to: '/admin/case-referrals', label: 'Referrals', Icon: Send },
  { to: '/admin/referrals', label: 'Links', Icon: Link2 },
];

export default function AdminShell() {
  const { profile, signOut } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem('pi-portal-theme') || 'light');
  const [mobileNav, setMobileNav] = useState(false);

  useSessionTimeout();

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('pi-portal-theme', next);
      return next;
    });
  };

  const initials = profile
    ? `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase() || 'A'
    : 'A';

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 md:px-7 h-[60px] bg-secondary border-b border-theme sticky top-0 z-50 backdrop-blur-xl">
        {/* Left: burger + logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileNav(!mobileNav)}
            className="lg:hidden w-9 h-9 rounded-[10px] bg-tertiary border border-theme flex items-center justify-center hover:bg-hover-surface transition-all"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              {mobileNav ? <><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></> : <><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="14" y2="8"/><line x1="2" y1="12" x2="14" y2="12"/></>}
            </svg>
          </button>
          <NavLink to="/admin" className="flex items-center gap-2.5">
            <div className="w-[34px] h-[34px] bg-[var(--danger)] rounded-[10px] flex items-center justify-center text-[13px] font-extrabold text-white">
              ML
            </div>
            <span className="hidden sm:inline text-primary-c font-bold text-[16px]">Admin Panel</span>
          </NavLink>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex gap-0.5 bg-tertiary p-1 rounded-[10px]">
          {adminNavItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-3.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-accent text-white shadow-glow'
                    : 'text-muted-c hover:text-primary-c'
                }`
              }
            >
              <item.Icon size={14} strokeWidth={2.2} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          <NavLink
            to="/app"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] bg-tertiary border border-theme text-[12px] font-medium text-secondary-c hover:bg-hover-surface transition-all"
          >
            Back to App
          </NavLink>
          <button
            onClick={toggleTheme}
            className="hidden sm:flex w-9 h-9 rounded-[10px] border border-theme bg-tertiary text-muted-c items-center justify-center hover:bg-hover-surface hover:text-accent-light transition-all hover:rotate-12"
          >
            {theme === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-[34px] h-[34px] rounded-[10px] bg-[var(--danger)] flex items-center justify-center text-[12px] font-semibold text-white">
              {initials}
            </div>
            <button
              onClick={signOut}
              className="hidden md:block text-[13px] font-medium text-muted-c hover:text-danger transition-all"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileNav && (
        <>
          <div className="fixed inset-0 bg-overlay z-40 lg:hidden" onClick={() => setMobileNav(false)} />
          <div className="fixed top-[60px] left-0 right-0 bg-secondary border-b border-theme z-50 lg:hidden animate-fade-in">
            <nav className="flex flex-col py-2">
              {adminNavItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileNav(false)}
                  className={({ isActive }) =>
                    `px-6 py-3 text-[14px] font-medium transition-all border-l-2 flex items-center gap-2.5 ${
                      isActive ? 'text-accent-light border-accent bg-accent-glow' : 'text-secondary-c border-transparent hover:bg-hover-surface'
                    }`
                  }
                >
                  <item.Icon size={16} strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/app"
                onClick={() => setMobileNav(false)}
                className="px-6 py-3 text-[14px] font-medium text-secondary-c border-l-2 border-transparent hover:bg-hover-surface transition-all"
              >
                Back to App
              </NavLink>
              <button
                onClick={() => { setMobileNav(false); signOut(); }}
                className="px-6 py-3 text-[14px] font-medium text-danger text-left border-l-2 border-transparent hover:bg-hover-surface transition-all"
              >
                Sign Out
              </button>
            </nav>
          </div>
        </>
      )}

      <Outlet />
    </div>
  );
}
